import { Typography } from '@mui/material';
import React from 'react';

/**
 * Strips chords from a line of text
 * @param {string} line - The line to strip chords from
 * @returns {string} - The line with chords removed
 */
export const stripChords = (line) => {
  // First check if the line only contains chords
  const isChordLine = /^[A-G][#b]?(?:m|M|maj|dim|aug|sus[24]|[567])?(?:\/[A-G][#b]?)?\s*(?:[A-G][#b]?(?:m|M|maj|dim|aug|sus[24]|[567])?(?:\/[A-G][#b]?)?\s*)*$/.test(line.trim());
  if (isChordLine) {
    return '';
  }

  // Remove content between parentheses
  let result = line.replace(/\([^)]*\)/g, '');
  // Remove chord patterns
  result = result.replace(/\b[A-G][#b]?(?:m|M|maj|dim|aug|sus[24]|[567])?(?:\/[A-G][#b]?)?\b/g, '');
  // Remove any remaining chord-like patterns
  result = result.replace(/[A-G][#b]?(?:m|M|maj|dim|aug|sus[24]|[567])?(?:\/[A-G][#b]?)?/g, '');
  // Remove extra whitespace and trim
  result = result.replace(/\s+/g, ' ').trim();
  return result;
};

/**
 * Checks if a line is a guitar tab
 * @param {string} line - The line to check
 * @returns {boolean} - True if the line is a guitar tab
 */
export const isGuitarTab = (line) => /[|-][0-9-|]+/.test(line);

/**
 * Checks if a line contains only chords
 * @param {string} line - The line to check
 * @returns {boolean} - True if the line contains only chords
 */
export const isChordLine = (line) => {
  // Trim the line and check if it's empty
  const trimmedLine = line.trim();
  if (!trimmedLine) return false;
  
  // Split by whitespace and check if each part looks like a chord
  const parts = trimmedLine.split(/\s+/);
  
  // Check if all parts match a chord pattern
  return parts.every(part => {
    // Basic chord pattern: starts with A-G, can have #/b, followed by various modifiers
    // Handles complex chords like C#m7(9), Gsus4[add9], etc.
    // Also handles slash chords like A/C#
    return /^[A-G][#b]?(?:m|M|maj|min|dim|aug|sus[24]|[0-9])*(?:\([^)]*\)|\[[^\]]*\]|\{[^}]*\})*(?:\/[A-G][#b]?)?$/.test(part);
  });
};

/**
 * Formats a line from the chord chart with appropriate styling
 * @param {string} line - The line to format
 * @param {boolean} showLyricsOnly - Whether to show only lyrics
 * @param {string} transposedLine - The transposed version of the line
 * @returns {JSX.Element|null} - The formatted line
 */
export const formatChordChartLine = (line, { showLyricsOnly, transposedLine = line }) => {
  // Handle empty lines - always show them to preserve spacing
  if (!line.trim()) {
    return <Typography component="div" sx={{ whiteSpace: 'pre-wrap' }}>&nbsp;</Typography>;
  }

  // Handle section markers - always show them
  if (line.trim().match(/^\[.*\]$/)) {
    return <Typography component="div" sx={{ whiteSpace: 'pre-wrap' }}>{line}</Typography>;
  }

  // If showing lyrics only, hide chord lines and guitar tabs
  if (showLyricsOnly) {
    if (isChordLine(line) || isGuitarTab(line)) {
      return <Typography component="div" sx={{ display: 'none' }}>{line}</Typography>;
    }
    // For lyrics lines, show them normally but collapse multiple spaces
    // This preserves indentation but removes excessive spacing
    const cleanedLine = line.replace(/\s{2,}/g, ' ');
    return <Typography component="div" sx={{ whiteSpace: 'pre-wrap' }}>{cleanedLine}</Typography>;
  }

  // Not in lyrics-only mode, show everything with appropriate styling
  if (isGuitarTab(line)) {
    return (
      <Typography
        component="div"
        className="guitarTab"
        sx={{
          fontFamily: 'monospace',
          color: 'purple',
          fontSize: {
            xs: '0.75em',
            sm: '0.85em',
            md: '1em',
          },
          overflowX: {
            xs: 'auto',
            sm: 'auto',
            md: 'visible'
          },
          whiteSpace: 'nowrap',
        }}
      >
        {transposedLine}
      </Typography>
    );
  }
  
  if (isChordLine(line)) {
    return (
      <Typography
        component="div"
        className="chordLine"
        sx={{
          fontFamily: 'monospace',
          fontWeight: 'bold',
          fontSize: '1.1em',
          whiteSpace: 'pre-wrap',
        }}
      >
        {transposedLine}
      </Typography>
    );
  }

  return (
    <Typography
      component="div"
      className="lyricsLine"
      sx={{
        fontFamily: 'monospace',
        fontSize: '1.1em',
        whiteSpace: 'pre-wrap',
      }}
    >
      {transposedLine}
    </Typography>
  );
};
